import { useEffect, useState } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';
import type { ApiClient } from '../api';
import { Fachaktionen } from './Fachaktionen';
import { IconOk, IconWarnung, IconHerunterladen } from '../Icons';
import { LocalTabs } from '../rahmen/LocalTabs';
import { SprinklerGeraete } from './SprinklerGeraete';
import { GewerkUnterlagenZip } from '../UnterlagenZipKnopf';

/**
 * Die Sprinkleranlage.
 *
 * --- Warum die Köpfe ohne Netz ganz oben stehen ----------------------------
 *
 * 2.159 von 5.352 Sprinklern tragen keinen Systemnamen. Sie sind der Grund,
 * warum eine Wirkflächenberechnung heute noch kein Nachweis wäre: Eine
 * Wirkfläche, die vierzig Prozent der Anlage nicht kennt, liegt zufällig in
 * einem erfassten Bereich.
 *
 * Der Leselauf unterscheidet die beiden möglichen Ursachen, und sie führen zu
 * verschiedenen Antworten:
 *
 *   Kopf MIT verbundenem Connector, ohne Systemname
 *       → hängt am Netz, nur die Zuordnung fehlt. Kein Modellfehler.
 *
 *   Kopf OHNE verbundenen Connector
 *       → hängt an nichts. Modellbefund, Blocker.
 *
 * --- Warum die drei Netze getrennt bleiben ---------------------------------
 *
 * Eine nasse Steigleitung wird nach 3 × 200 l/min bemessen, ein Sprinklernetz
 * nach Wirkfläche. Beide zusammenzurechnen wäre ein Bemessungsfehler — und
 * einer, der nicht auffällt, weil eine Zahl herauskommt.
 */

interface Rahmen { api: ApiClient; projekt?: { id: string } }

const NETZNAMEN: Record<string, string> = {
  sprinklernetz_nass: 'Sprinklernetz nass',
  steigleitung_nass: 'Steigleitung nass (Wandhydranten)',
  steigleitung_trocken: 'Steigleitung trocken',
};

const LAGE: Record<string, string> = {
  upright: 'Upright (stehend)',
  pendent: 'Pendent (hängend)',
  sidewall: 'Sidewall (seitlich)',
};

/** Kontext, den `SprinklerRahmen` per `Outlet` an jede Unterseite weiterreicht. */
export interface SprinklerKontext {
  api: ApiClient;
  projektId: string;
  u: any;
}

/**
 * Der Rahmen um die zehn Sprinkler-Seiten (Paket N2, Muster N1) — Reiter
 * als echte Routen statt `useState('bestand')`.
 *
 * Fachaktionen, Fehleranzeige und Sperren gelten für ALLE zehn Unterseiten
 * gleich und bleiben deshalb hier oben stehen, wie zuvor — nur der reine
 * Reiterinhalt wandert in eigene Routen mit `Outlet`. Acht der zehn
 * Reiterkomponenten waren schon vor N2 self-fetching (nur `api`+
 * `projektId`); nur `Bestand` und `Konfiguration` brauchen die
 * Kennzahlenübersicht `u` und bekommen sie über den Outlet-Kontext.
 */
export function SprinklerRahmen() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [u, setU] = useState<any | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!projekt) return;

    api.sprinklerUebersicht(projekt.id)
      .then((x) => { setU(x); setFehler(null); })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projekt?.id]);

  if (!projekt) return <div className="leer">wird geladen …</div>;

  const leer = !u || u.koepfe === 0;

  return (
    <>
      {/* Die Auftragsart 110. */}
      <Fachaktionen
        api={api}
        rolleKey="sprinkler"
        leseArt={110}
        leseText="Modell lesen"
      >
        <GewerkUnterlagenZip api={api} projektId={projekt.id} gewerk="sprinkler" />
      </Fachaktionen>

      {fehler && <div className="hinweis fehler">{fehler}</div>}

      {u && u.sperren?.length > 0 && <Sperren sperren={u.sperren} />}

      {leer ? (
        <div className="karte">
          <div className="leer">
            Noch kein Sprinklerbestand. Er entsteht aus einem Auftrag mit der
            Sprinkleraufnahme — oben „Modell lesen“.
            <p style={{ marginTop: 12 }}>
              Der Lauf liest <em>drei getrennte Netze</em>: das Sprinklernetz
              nass, die nasse Steigleitung der Wandhydranten und die
              Trockenleitung. Sie bleiben getrennt, weil sie verschieden
              bemessen werden — die Steigleitung nach 3 × 200 l/min, das
              Sprinklernetz nach Wirkfläche.
            </p>
            <p className="schwach" style={{ fontSize: 12 }}>
              Erwartet werden rund 17.790 Rohre, 5.352 Sprinkler, 4
              Feuerlöschpumpen und 73 Wandhydrantenschränke. Der Lauf zieht
              die Topologie aus den Connectoren und beantwortet damit die
              Frage, ob die 2.159 Köpfe ohne Systemnamen wirklich an nichts
              hängen.
            </p>
          </div>
        </div>
      ) : (
        <>
          <LocalTabs
            ariaLabel="Sprinkler"
            eintraege={[
              { to: '.', beschriftung: 'Bestand', end: true },
              { to: 'raeume', beschriftung: 'Ebenen und Räume' },
              { to: 'netze', beschriftung: 'Netze' },
              { to: 'koepfe', beschriftung: 'Sprinkler' },
              { to: 'berechnung', beschriftung: 'Berechnung' },
              { to: 'nachweis', beschriftung: 'Wege und Teilstrecken' },
              { to: 'steigleitung', beschriftung: 'Steigleitung' },
              { to: 'geraete', beschriftung: 'Geräte' },
              { to: 'technik', beschriftung: 'Anlagentechnik' },
              { to: 'konfiguration', beschriftung: 'Konfiguration' },
            ]}
          />

          <Outlet context={{ api, projektId: projekt.id, u } satisfies SprinklerKontext} />
        </>
      )}
    </>
  );
}

export function SprinklerBestandSeite() {
  const { u } = useOutletContext<SprinklerKontext>();
  return <Bestand u={u} />;
}

export function SprinklerRaeumeSeite() {
  const { api, projektId } = useOutletContext<SprinklerKontext>();
  return <Raumbaum api={api} projektId={projektId} />;
}

export function SprinklerNetzeSeite() {
  const { api, projektId } = useOutletContext<SprinklerKontext>();
  return <Netze api={api} projektId={projektId} />;
}

export function SprinklerKoepfeSeite() {
  const { api, projektId } = useOutletContext<SprinklerKontext>();
  return <Koepfe api={api} projektId={projektId} />;
}

export function SprinklerBerechnungSeite() {
  const { api, projektId } = useOutletContext<SprinklerKontext>();
  return <Berechnung api={api} projektId={projektId} />;
}

/** Die Aufschlüsselung (Schritt 4c): Anlage → Station → Wirkfläche → Weg → Teilstrecke → Posten. */
export function SprinklerNachweisSeite() {
  const { api, projektId } = useOutletContext<SprinklerKontext>();
  return <Nachweisbaum api={api} projektId={projektId} />;
}

export function SprinklerSteigleitungSeite() {
  const { api, projektId } = useOutletContext<SprinklerKontext>();
  return <Berechnung api={api} projektId={projektId} bereich="steigleitung" />;
}

export function SprinklerGeraeteSeite() {
  const { api, projektId } = useOutletContext<SprinklerKontext>();
  return <SprinklerGeraete api={api} projektId={projektId} />;
}

export function SprinklerTechnikSeite() {
  const { api, projektId } = useOutletContext<SprinklerKontext>();
  return <Technik api={api} projektId={projektId} />;
}

export function SprinklerKonfigurationSeite() {
  const { u } = useOutletContext<SprinklerKontext>();
  return <Konfiguration u={u} />;
}

function Sperren({ sperren }: { sperren: any[] }) {
  const blocker = sperren.filter((s) => s.gewicht === 'blocker');

  // Die linke Kante sagt dasselbe wie die Titel darunter: Sperre oder
  // offener Punkt. Sie trug bis 19.09.2026 #dc2626 - ein drittes Rot neben
  // dem #b91c1c der Titel IN DERSELBEN KARTE, und zur Marke bei ΔE00 = 4,93.
  // Jetzt dasselbe Merkmal wie die Titel. Das Bernstein der offenen Punkte
  // bleibt ein Literal: Warnung ist vom Markenwechsel nicht berührt.
  return (
    <div className="karte" style={{
      borderLeft: `3px solid ${blocker.length > 0 ? 'var(--zustand-fehler)' : '#f59e0b'}`,
    }}>
      <h3 style={{ marginTop: 0 }}>
        {blocker.length > 0
          ? 'Die Wirkflächenberechnung wäre noch kein Nachweis'
          : 'Offene Punkte'}
      </h3>

      {sperren.map((s) => (
        <div key={s.kennung} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
            {s.gewicht === 'blocker'
              ? <IconWarnung size={14} />
              : <span style={{ color: '#b45309' }}>▲</span>}
            <strong style={{
              color: s.gewicht === 'blocker' ? 'var(--zustand-fehler)' : '#b45309',
            }}>
              {s.titel}
            </strong>
          </div>
          <p style={{ margin: '4px 0 0 22px', fontSize: 13 }}>{s.text}</p>
        </div>
      ))}
    </div>
  );
}

function Bestand({ u }: { u: any }) {
  const z = (x: number, n = 0) =>
    (x ?? 0).toLocaleString('de-DE', { minimumFractionDigits: n,
                                       maximumFractionDigits: n });

  return (
    <>
      <div className="karte">
        <h3 style={{ marginTop: 0 }}>Bestand</h3>

        <div className="kennzahlen">
          <div className="kennzahl">
            <div className="kennzahl-wert">{z(u.koepfe)}</div>
            <div className="kennzahl-name">Sprinkler</div>
          </div>
          <div className="kennzahl">
            <div className="kennzahl-wert">{z(u.rohre)}</div>
            <div className="kennzahl-name">Rohre und Formteile</div>
          </div>
          <div className="kennzahl">
            <div className="kennzahl-wert">{z(u.verbindungen)}</div>
            <div className="kennzahl-name">Verbindungen</div>
          </div>
          <div className="kennzahl">
            <div className="kennzahl-wert">{z(u.netze)}</div>
            <div className="kennzahl-name">Netze</div>
          </div>
          <div className="kennzahl">
            <div className="kennzahl-wert"
                 style={{ color: u.ohneConnector > 0 ? 'var(--zustand-fehler)'
                                                     : undefined }}>
              {z(u.ohneConnector)}
            </div>
            <div className="kennzahl-name">ohne Connector</div>
          </div>
          <div className="kennzahl">
            <div className="kennzahl-wert"
                 style={{ color: u.ohneNetz > 0 ? '#b45309' : undefined }}>
              {z(u.ohneNetz)}
            </div>
            <div className="kennzahl-name">ohne Systemname</div>
          </div>
        </div>
      </div>

      <div className="karte">
        <h3 style={{ marginTop: 0 }}>Die drei Netze</h3>

        <p className="schwach" style={{ fontSize: 12, marginTop: 0 }}>
          Sie bleiben getrennt, weil sie verschieden bemessen werden. Eine
          nasse Steigleitung nach 3&nbsp;×&nbsp;200&nbsp;l/min, ein
          Sprinklernetz nach Wirkfläche — beide zusammenzurechnen wäre ein
          Bemessungsfehler, und einer, der nicht auffällt.
        </p>

        <table className="zuordnung">
          <thead>
            <tr>
              <th>Netz</th>
              <th style={{ textAlign: 'right' }}>Rohre</th>
              <th style={{ textAlign: 'right' }}>Länge [m]</th>
              <th style={{ textAlign: 'right' }}>Sprinkler</th>
              <th>Bemessung nach</th>
            </tr>
          </thead>
          <tbody>
            {(u.jeNetzart ?? []).map((n: any) => (
              <tr key={n.netzArt}>
                <td><strong>{NETZNAMEN[n.netzArt] ?? n.netzArt}</strong></td>
                <td style={{ textAlign: 'right' }}>{z(n.stueck)}</td>
                <td style={{ textAlign: 'right' }}>{z(n.laengeM, 1)}</td>
                <td style={{ textAlign: 'right' }}>{z(n.koepfe)}</td>
                <td className="schwach">
                  {n.netzArt === 'sprinklernetz_nass'
                    ? 'Wirkfläche'
                    : n.netzArt === 'steigleitung_nass'
                      ? '3 × 200 l/min über 120 min'
                      : 'gesondert'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="karte">
        <h3 style={{ marginTop: 0 }}>Die Köpfe</h3>

        <div style={{ display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                      gap: 16 }}>
          <div>
            <h4 style={{ margin: '0 0 6px' }}>nach Einbaulage</h4>
            <table className="zuordnung" style={{ fontSize: 12 }}>
              <tbody>
                {(u.jeEinbaulage ?? []).map((e: any, i: number) => (
                  <tr key={i}>
                    <td>{LAGE[e.einbaulage] ?? e.einbaulage ?? '—'}</td>
                    <td style={{ textAlign: 'right' }}>{z(e.stueck)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <h4 style={{ margin: '0 0 6px' }}>nach K-Faktor</h4>
            <table className="zuordnung" style={{ fontSize: 12 }}>
              <tbody>
                {(u.jeKFaktor ?? []).map((e: any, i: number) => (
                  <tr key={i}>
                    <td>
                      {e.kFaktor
                        ? `K ${e.kFaktor}`
                        : <em className="schwach">nicht gelesen</em>}
                    </td>
                    <td style={{ textAlign: 'right' }}>{z(e.stueck)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="schwach" style={{ fontSize: 11, marginTop: 4 }}>
              Aus dem Typnamen über die Musterregel — im Modell ist die
              Spalte K-Faktor leer.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

function Netze({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [netze, setNetze] = useState<any[] | null>(null);
  const [art, setArt] = useState('');

  useEffect(() => {
    api.sprinklerNetze(projektId, art || undefined)
      .then(setNetze)
      .catch(() => setNetze([]));
  }, [api, projektId, art]);

  if (!netze) return <div className="leer">wird geladen …</div>;

  return (
    <div className="karte">
      <h3 style={{ marginTop: 0 }}>
        Netze
        <span className="schwach" style={{ fontWeight: 400, fontSize: 13 }}>
          {' '}({netze.length})
        </span>
      </h3>

      <div style={{ marginBottom: 10 }}>
        <select value={art} onChange={(e) => setArt(e.target.value)}>
          <option value="">alle drei Netzarten</option>
          {Object.entries(NETZNAMEN).map(([k, n]) => (
            <option key={k} value={k}>{n}</option>
          ))}
        </select>
      </div>

      <table className="zuordnung">
        <thead>
          <tr>
            <th>Systemname</th><th>Netzart</th><th>Systemtyp</th>
            <th style={{ textAlign: 'right' }}>Rohre</th>
            <th style={{ textAlign: 'right' }}>Länge [m]</th>
            <th style={{ textAlign: 'right' }}>Sprinkler</th>
          </tr>
        </thead>
        <tbody>
          {netze.slice(0, 300).map((n: any) => (
            <tr key={n.id}>
              <td><strong>{n.systemname ?? '—'}</strong></td>
              <td className="schwach">
                {NETZNAMEN[n.art] ?? n.art}
              </td>
              <td className="schwach">{n.systemtyp ?? '—'}</td>
              <td style={{ textAlign: 'right' }}>{n.rohre}</td>
              <td style={{ textAlign: 'right' }}>
                {n.laengeM?.toLocaleString('de-DE', {
                  maximumFractionDigits: 1 }) ?? '—'}
              </td>
              <td style={{ textAlign: 'right' }}>{n.koepfe ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Koepfe({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [daten, setDaten] = useState<any | null>(null);
  const [seite, setSeite] = useState(1);
  const [nurOhne, setNurOhne] = useState(false);

  useEffect(() => {
    api.sprinklerKoepfe(projektId, { seite, nurOhneNetz: nurOhne })
      .then(setDaten)
      .catch(() => setDaten(null));
  }, [api, projektId, seite, nurOhne]);

  if (!daten) return <div className="leer">wird geladen …</div>;

  const seiten = Math.ceil(daten.gesamt / daten.proSeite);

  return (
    <div className="karte">
      <h3 style={{ marginTop: 0 }}>
        Sprinkler
        <span className="schwach" style={{ fontWeight: 400, fontSize: 13 }}>
          {' '}({daten.gesamt.toLocaleString('de-DE')})
        </span>
      </h3>

      <label style={{ display: 'block', marginBottom: 10, fontSize: 13 }}>
        <input type="checkbox" checked={nurOhne}
               onChange={(e) => { setNurOhne(e.target.checked); setSeite(1); }} />
        {' '}nur die ohne Systemnamen
      </label>

      <table className="zuordnung" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th>Element</th><th>Typ</th>
            <th style={{ textAlign: 'right' }}>K</th>
            <th style={{ textAlign: 'right' }}>DN</th>
            <th>Einbaulage</th><th>Geschoss</th><th>Raum</th>
            <th>Netz</th><th>Connector</th>
          </tr>
        </thead>
        <tbody>
          {(daten.zeilen ?? []).map((k: any) => (
            <tr key={k.id}
                style={k.connectorVerbunden === false
                  ? { background: '#fef2f2' } : undefined}>
              <td>{k.revitElementId}</td>
              <td className="schwach">{k.typ ?? '—'}</td>
              <td style={{ textAlign: 'right' }}>{k.kFaktor ?? '—'}</td>
              <td style={{ textAlign: 'right' }}>{k.nennweite ?? '—'}</td>
              <td className="schwach">
                {LAGE[k.einbaulage]?.split(' ')[0] ?? k.einbaulage ?? '—'}
              </td>
              <td>{k.geschoss ?? '—'}</td>
              <td className="schwach">
                {k.raumnummer
                  ? `${k.raumnummer} ${k.raumname ?? ''}`.trim()
                  : <em>—</em>}
              </td>
              <td>
                {k.systemname ?? <em className="schwach">ohne</em>}
              </td>
              <td>
                {k.connectorVerbunden === true ? <IconOk size={12} />
                 : k.connectorVerbunden === false
                   ? <span title={k.befund ?? undefined}>
                       <IconWarnung size={12} />
                     </span>
                   : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {seiten > 1 && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center',
                      marginTop: 10, fontSize: 13 }}>
          <button type="button" disabled={seite <= 1}
                  onClick={() => setSeite(seite - 1)}>← zurück</button>
          <span className="schwach">Seite {seite} von {seiten}</span>
          <button type="button" disabled={seite >= seiten}
                  onClick={() => setSeite(seite + 1)}>weiter →</button>
        </div>
      )}
    </div>
  );
}

function Technik({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const [t, setT] = useState<any | null>(null);

  useEffect(() => {
    api.sprinklerAnlagentechnik(projektId)
      .then(setT)
      .catch(() => setT(null));
  }, [api, projektId]);

  if (!t) return <div className="leer">wird geladen …</div>;

  const pumpen = t.pumpen ?? [];

  return (
    <>
      <div className="karte">
        <h3 style={{ marginTop: 0 }}>
          Feuerlöschpumpen
          <span className="schwach" style={{ fontWeight: 400, fontSize: 13 }}>
            {' '}({pumpen.length})
          </span>
        </h3>

        <p className="schwach" style={{ fontSize: 12, marginTop: 0 }}>
          Bei einer Mehrpumpenanlage gilt <strong>n−1</strong>: Bei Ausfall
          einer Pumpe müssen die übrigen die Auslegung tragen. Das ist die
          Anforderung, für die eine Mehrpumpenanlage gebaut wird — und sie
          lässt sich erst prüfen, wenn Förderstrom und Förderhöhe je Pumpe
          vorliegen.
        </p>

        {pumpen.length === 0 ? (
          <div className="leer">Keine Pumpe im Bestand.</div>
        ) : (
          <table className="zuordnung">
            <thead>
              <tr>
                <th>Kennung</th><th>Typ</th><th>Hersteller</th>
                <th style={{ textAlign: 'right' }}>Q [l/min]</th>
                <th style={{ textAlign: 'right' }}>H [m]</th>
                <th style={{ textAlign: 'right' }}>P [kW]</th>
                <th>Geschoss</th>
              </tr>
            </thead>
            <tbody>
              {pumpen.map((p: any) => (
                <tr key={p.id}>
                  <td><strong>{p.kennung ?? p.revitElementId}</strong></td>
                  <td className="schwach">{p.typ ?? '—'}</td>
                  <td className="schwach">{p.hersteller ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    {p.foerderstromLMin ?? '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {p.foerderhoeheM ?? '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>{p.leistungKw ?? '—'}</td>
                  <td>{p.geschoss ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {pumpen.length > 0 && pumpen.every((p: any) => !p.foerderstromLMin) && (
          <p style={{ fontSize: 12, color: '#b45309', marginBottom: 0 }}>
            Keine Pumpe trägt einen Förderstrom. Die n−1-Prüfung braucht
            Kennfelder aus den Herstellerdaten — als Kennfeld, nicht als
            einzelnen Punkt: Wird die Wirkfläche später geändert, muss neu
            nachgeschlagen werden.
          </p>
        )}
      </div>

      <div className="karte">
        <h3 style={{ marginTop: 0 }}>
          Wandhydranten
          <span className="schwach" style={{ fontWeight: 400, fontSize: 13 }}>
            {' '}({(t.hydranten ?? []).length})
          </span>
        </h3>

        <p className="schwach" style={{ fontSize: 12, marginTop: 0 }}>
          Sie gehören zur nassen Steigleitung, nicht zum Sprinklernetz. Die
          drei gleichzeitig zu betrachtenden sind die drei
          <strong> ungünstigsten</strong> — das folgt aus dem Druckbedarf und
          nicht aus der Reihenfolge.
        </p>

        <table className="zuordnung" style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Kennung</th><th>Typ</th><th>Geschoss</th><th>Raum</th>
              <th style={{ textAlign: 'right' }}>Fließdruck [bar]</th>
              <th>maßgebend</th>
            </tr>
          </thead>
          <tbody>
            {(t.hydranten ?? []).slice(0, 100).map((h: any) => (
              <tr key={h.id}
                  style={h.istMassgebend ? { background: '#fefce8' }
                                         : undefined}>
                <td><strong>{h.kennung ?? h.revitElementId}</strong></td>
                <td className="schwach">{h.typ ?? '—'}</td>
                <td>{h.geschoss ?? '—'}</td>
                <td className="schwach">{h.raumnummer ?? '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  {h.fliessdruckBar ?? '—'}
                </td>
                <td>{h.istMassgebend ? <IconWarnung size={12} /> : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(t.stationen ?? []).length > 0 && (
        <div className="karte">
          <h3 style={{ marginTop: 0 }}>
            Alarmventilstationen
            <span className="schwach" style={{ fontWeight: 400,
                                               fontSize: 13 }}>
              {' '}({t.stationen.length})
            </span>
          </h3>

          <table className="zuordnung" style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th>Kennung</th><th>Typ</th><th>Geschoss</th><th>Raum</th>
              </tr>
            </thead>
            <tbody>
              {t.stationen.map((s: any) => (
                <tr key={s.id}>
                  <td><strong>{s.kennung ?? s.revitElementId}</strong></td>
                  <td className="schwach">{s.typ ?? '—'}</td>
                  <td>{s.geschoss ?? '—'}</td>
                  <td className="schwach">{s.raumnummer ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/**
 * Der Baum Ebene → Raum → Sprinkler, mit der Prüfrechnung je Raum.
 *
 * --- Was die Prüfrechnung ist, und was nicht -------------------------------
 *
 * Raumfläche geteilt durch 10,5 m² ergibt die Mindestanzahl an Köpfen.
 * Aufgerundet wird erst ab einem Zehntel über der ganzen Zahl: 4,05 bleibt
 * 4, 4,10 wird 5. Ohne diese Schwelle schlüge die Liste bei jedem Raum an,
 * der die Grenze um Zentimeter reißt — und eine Liste, die überall
 * anschlägt, liest niemand mehr.
 *
 * Sie ist eine Prüfrechnung und kein Nachweis. Sie findet die Räume, in
 * denen offensichtlich zu wenige Köpfe stehen — über alle 606 Räume auf
 * einmal, was von Hand hieße, 606 Grundrisse aufzuschlagen. Ob die Anlage
 * bemessen ist, sagt sie nicht: Dazu gehören Wirkfläche, Beaufschlagung und
 * der Druck am ungünstigsten Kopf.
 *
 * --- Warum beanstandete Räume oben stehen ----------------------------------
 *
 * Wer eine Etage aufklappt, will sehen, wo etwas fehlt. Alphabetisch
 * sortiert müsste er scrollen, um es zu finden.
 */
function Raumbaum({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const [d, setD] = useState<any | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [nurBeanstandet, setNurBeanstandet] = useState(false);

  useEffect(() => {
    api.sprinklerBaum(projektId)
      .then((x) => { setD(x); setFehler(null); })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projektId]);

  if (fehler) return <div className="hinweis fehler">{fehler}</div>;
  if (!d) return <div className="leer">wird geladen …</div>;

  const umschalten = (k: string) => setOffen((v) => {
    const n = new Set(v);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  const z = (x: number, n = 0) =>
    (x ?? 0).toLocaleString('de-DE', {
      minimumFractionDigits: n, maximumFractionDigits: n,
    });

  return (
    <>
      <div className="karte">
        <div className="kennzahlen">
          <div className="kennzahl">
            <div className="kennzahl-wert">{z(d.koepfe)}</div>
            <div className="kennzahl-name">Sprinkler</div>
          </div>
          <div className="kennzahl">
            <div className="kennzahl-wert">{z(d.raeume)}</div>
            <div className="kennzahl-name">Räume</div>
          </div>
          <div className="kennzahl">
            <div className="kennzahl-wert"
                 style={{ color: d.beanstandet > 0 ? '#b45309' : undefined }}>
              {z(d.beanstandet)}
            </div>
            <div className="kennzahl-name">beanstandet</div>
          </div>
          <div className="kennzahl">
            <div className="kennzahl-wert"
                 style={{ color: d.ohneRaum > 0 ? '#b45309' : undefined }}>
              {z(d.ohneRaum)}
            </div>
            <div className="kennzahl-name">Köpfe ohne Raum</div>
          </div>
        </div>

        <p className="schwach" style={{ fontSize: 12, marginBottom: 0 }}>
          Prüfrechnung je Raum: Fläche ÷ {z(d.flaecheJeKopfM2, 1)} m² =
          erforderliche Mindestanzahl. Aufgerundet wird erst ab{' '}
          {z(d.aufrundungsschwelle, 1)} über der ganzen Zahl — 4,05 bleibt 4,
          4,10 wird 5. Das ist eine Prüfrechnung und kein Nachweis: Sie
          findet Räume mit offensichtlich zu wenigen Köpfen, nicht die
          Bemessung der Anlage.
        </p>

        {d.hohlraumkoepfe > 0 && (
          <p className="schwach" style={{ fontSize: 12, marginTop: 8,
                                          marginBottom: 0 }}>
            <strong>{z(d.hohlraumkoepfe)} Hohlraumsprinkler</strong> zählen in
            dieser Prüfrechnung nicht mit — Festlegung des Fachplaners vom
            21.09.2026: „nach VdS CEA sind Hohlraumsprinkler in der Bemessung
            nicht zu berücksichtigen und Abstände dieser nicht relevant." Die
            {' '}{z(d.flaecheJeKopfM2, 1)} m² gelten dem <em>Raum</em>, und ein
            Kopf über der Abhangdecke deckt davon nichts ab. In den Listen,
            den Stückzahlen und im Leistungsverzeichnis stehen sie
            unverändert; sie werden gebaut, sie bemessen nur nicht.
          </p>
        )}

        {d.ohneRaum > 0 && (
          <div className="hinweis warnung" style={{ marginTop: 12 }}>
            <IconWarnung size={15} />
            <div>
              <strong>{z(d.ohneRaum)} Sprinkler tragen keine Raumnummer.</strong>{' '}
              Sie fehlen in jeder Raumzeile — und dort, wo ein großer Raum
              nur ein oder zwei Köpfe zeigt, liegt der Fehlbetrag meist
              daran und nicht an der Planung. „Merchandise E4-072" hat
              8.026 m² und einen Kopf; die übrigen sind erfasst, nur keinem
              Raum zugeordnet. Die Zeilen mit sehr großem Fehlbetrag sind
              deshalb zuerst eine Frage an die Raumzuordnung.
            </div>
          </div>
        )}
      </div>

      <div className="karte">
        <label style={{ display: 'flex', gap: 6, alignItems: 'center',
                        fontSize: 13, marginBottom: 10 }}>
          <input type="checkbox" checked={nurBeanstandet}
                 onChange={(e) => setNurBeanstandet(e.target.checked)} />
          Nur beanstandete Räume
        </label>

        {d.ebenen.map((e: any) => {
          const zimmer = nurBeanstandet
            ? e.zimmer.filter((r: any) => !r.reicht)
            : e.zimmer;

          if (nurBeanstandet && zimmer.length === 0) return null;

          const auf = offen.has(e.geschoss);

          return (
            <div key={e.geschoss} style={{ marginBottom: 6 }}>
              <button type="button"
                      onClick={() => umschalten(e.geschoss)}
                      style={{
                        display: 'flex', gap: 8, alignItems: 'baseline',
                        width: '100%', textAlign: 'left', background: 'none',
                        border: 'none', borderBottom: '1px solid var(--linie)',
                        padding: '6px 0', cursor: 'pointer',
                      }}>
                <span style={{ width: 12 }}>{auf ? '▾' : '▸'}</span>
                <strong>{e.geschoss}</strong>
                <span className="schwach" style={{ fontSize: 12 }}>
                  {z(e.koepfe)} Sprinkler · {z(e.raeume)} Räume
                </span>
                {e.beanstandet > 0 && (
                  <span style={{ color: '#b45309', fontSize: 12,
                                 marginLeft: 'auto' }}>
                    <IconWarnung size={13} /> {z(e.beanstandet)} beanstandet
                  </span>
                )}
              </button>

              {auf && zimmer.map((r: any) => (
                <Raumzeile key={r.raumnummer ?? 'ohne'} r={r}
                           offen={offen} umschalten={umschalten}
                           geschoss={e.geschoss} z={z} />
              ))}
            </div>
          );
        })}
      </div>
    </>
  );
}

function Raumzeile({ r, offen, umschalten, geschoss, z }: {
  r: any; offen: Set<string>; umschalten: (k: string) => void;
  geschoss: string; z: (x: number, n?: number) => string;
}) {
  const schluessel = `${geschoss}/${r.raumnummer ?? 'ohne'}`;
  const auf = offen.has(schluessel);

  return (
    <div style={{ marginLeft: 20, borderLeft: '1px solid var(--linie)',
                  paddingLeft: 10 }}>
      <button type="button" onClick={() => umschalten(schluessel)}
              style={{
                display: 'flex', gap: 8, alignItems: 'baseline', width: '100%',
                textAlign: 'left', background: 'none', border: 'none',
                padding: '4px 0', cursor: 'pointer',
              }}>
        <span style={{ width: 12 }}>{auf ? '▾' : '▸'}</span>

        <span style={{ minWidth: 78 }}>{r.raumnummer ?? '—'}</span>

        <span className="schwach" style={{ fontSize: 12, flex: 1 }}>
          {r.raumname}
        </span>

        {r.hatFlaeche && (
          <span className="schwach" style={{ fontSize: 12 }}>
            {z(r.flaecheM2, 1)} m² · {z(r.rechenwert, 2)} →{' '}
            {r.erforderlich} erf.
          </span>
        )}

        {r.hohlraumkoepfe > 0 && (
          <span className="schwach" style={{ fontSize: 12 }}>
            + {r.hohlraumkoepfe} Hohlraum
          </span>
        )}

        <span style={{
          fontSize: 12, fontWeight: 600, minWidth: 34, textAlign: 'right',
          color: r.reicht ? undefined : 'var(--zustand-fehler)',
        }}>
          {r.vorhanden}
        </span>
      </button>

      {r.hinweis && (
        <div className="hinweis warnung"
             style={{ margin: '2px 0 6px 20px', fontSize: 12 }}>
          <IconWarnung size={13} />
          <div>{r.hinweis}</div>
        </div>
      )}

      {!r.hatFlaeche && r.raumnummer && (
        <div className="schwach"
             style={{ margin: '2px 0 6px 20px', fontSize: 12 }}>
          Keine Fläche im Raumbuch — ohne sie ist die Prüfrechnung nicht
          möglich. Das ist etwas anderes als eine bestandene.
        </div>
      )}

      {auf && (
        <table className="tabelle" style={{ margin: '4px 0 8px 20px' }}>
          <thead>
            <tr>
              <th>Element</th><th>Typ</th><th>K</th><th>DN</th>
              <th>Einbaulage</th><th>Schutzbereich</th><th>System</th>
              <th>Anschluss</th>
            </tr>
          </thead>
          <tbody>
            {r.koepfe.map((k: any) => (
              <tr key={k.id}>
                <td>{k.revitElementId}</td>
                <td>{k.typ}</td>
                <td>{k.kFaktor ?? '—'}</td>
                <td>{k.nennweite ?? '—'}</td>
                <td>{LAGE[k.einbaulage] ?? k.einbaulage ?? '—'}</td>
                <td>
                  {k.schutzbereich === 'hohlraum'
                    ? <span className="schwach">Hohlraum (bemisst nicht)</span>
                    : k.schutzbereich === 'raum' ? 'Raum'
                    : <span className="schwach">—</span>}
                </td>
                <td>{k.systemname ?? <span className="schwach">ohne</span>}</td>
                <td>
                  {k.connectorVerbunden
                    ? <IconOk size={13} />
                    : <span style={{ color: 'var(--zustand-fehler)' }}>
                        <IconWarnung size={13} /> hängt an nichts
                      </span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Konfiguration({ u }: { u: any }) {
  const k = (u.konfiguration ?? [])[0];
  const s = u.steigleitung;

  const z = (x: number | null | undefined, n = 0) =>
    x == null ? '—' : x.toLocaleString('de-DE', {
      minimumFractionDigits: n, maximumFractionDigits: n });

  return (
    <>
      <div className="karte">
        <h3 style={{ marginTop: 0 }}>Sprinklernetz</h3>

        {!k ? (
          <div className="leer">Noch keine Konfiguration.</div>
        ) : (
          <>
            <table className="zuordnung">
              <tbody>
                <tr>
                  <td style={{ width: 240 }} className="schwach">Regelwerk</td>
                  <td><strong>
                    {k.regelwerk === 'vds_cea_4001'
                      ? 'VdS CEA 4001' : k.regelwerk}
                  </strong>
                  <span className="schwach">
                    {' '}— Druckverlust nach {
                      k.verfahren === 'darcy_weisbach'
                        ? 'Darcy-Weisbach' : 'Hazen-Williams'}
                  </span></td>
                </tr>
                <tr>
                  <td className="schwach">Brandrisiko</td>
                  <td><strong>{k.brandrisiko}</strong></td>
                </tr>
                <tr>
                  <td className="schwach">Wirkfläche</td>
                  <td>{z(k.wirkflaecheM2)} m²</td>
                </tr>
                <tr>
                  <td className="schwach">Wasserbeaufschlagung</td>
                  <td>{z(k.wasserbeaufschlagungMmMin, 1)} mm/min</td>
                </tr>
                <tr>
                  <td className="schwach">Wassermenge</td>
                  <td>
                    <strong>
                      {z(k.wirkflaecheM2 * k.wasserbeaufschlagungMmMin)} l/min
                    </strong>
                    <span className="schwach">
                      {' '}= {z(k.wirkflaecheM2)} × {z(
                        k.wasserbeaufschlagungMmMin, 1)}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="schwach">Bemessungsdauer</td>
                  <td>{z(k.bemessungsdauerMin)} min</td>
                </tr>
                <tr>
                  <td className="schwach">Vorrat Sprinkler</td>
                  <td><strong>
                    {z(k.wirkflaecheM2 * k.wasserbeaufschlagungMmMin
                       * k.bemessungsdauerMin / 1000, 1)} m³
                  </strong></td>
                </tr>
                <tr>
                  <td className="schwach">Versammlungsstätte</td>
                  <td>{k.versammlungsstaette ? 'ja' : 'nein'}</td>
                </tr>
                <tr>
                  <td className="schwach">Herkunft</td>
                  <td>
                    <strong>{k.herkunft}</strong>
                    {k.geprueft
                      ? <span> · geprüft</span>
                      : <span className="schwach"> · noch nicht geprüft</span>}
                  </td>
                </tr>
              </tbody>
            </table>

            {k.begruendung && (
              <p className="schwach" style={{ fontSize: 12, marginTop: 10 }}>
                {k.begruendung}
              </p>
            )}
          </>
        )}
      </div>

      <div className="karte">
        <h3 style={{ marginTop: 0 }}>Nasse Steigleitung</h3>

        {!s ? (
          <div className="leer">Noch keine Konfiguration.</div>
        ) : (
          <>
            <table className="zuordnung">
              <tbody>
                <tr>
                  <td style={{ width: 240 }} className="schwach">Regelwerk</td>
                  <td><strong>
                    {s.regelwerk === 'din_14462'
                      ? 'DIN 14462' : s.regelwerk}
                  </strong></td>
                </tr>
                <tr>
                  <td className="schwach">gleichzeitige Entnahmen</td>
                  <td>{s.entnahmenGleichzeitig}</td>
                </tr>
                <tr>
                  <td className="schwach">je Entnahme</td>
                  <td>{z(s.literProMinute)} l/min</td>
                </tr>
                <tr>
                  <td className="schwach">gesamt</td>
                  <td><strong>
                    {z(s.entnahmenGleichzeitig * s.literProMinute)} l/min
                  </strong></td>
                </tr>
                <tr>
                  <td className="schwach">Bemessungsdauer</td>
                  <td>{z(s.bemessungsdauerMin)} min</td>
                </tr>
                <tr>
                  <td className="schwach">Vorrat Steigleitung</td>
                  <td><strong>
                    {z(s.entnahmenGleichzeitig * s.literProMinute
                       * s.bemessungsdauerMin / 1000, 0)} m³
                  </strong></td>
                </tr>
                <tr>
                  <td className="schwach">Mindestfließdruck</td>
                  <td>{z(s.mindestfliessdruckBar, 1)} bar</td>
                </tr>
                <tr>
                  <td className="schwach">Herkunft</td>
                  <td><strong>{s.herkunft}</strong></td>
                </tr>
              </tbody>
            </table>

            {s.begruendung && (
              <p className="schwach" style={{ fontSize: 12, marginTop: 10 }}>
                {s.begruendung}
              </p>
            )}
          </>
        )}
      </div>

      <div className="karte" style={{ borderLeft: '3px solid #0891b2' }}>
        <h4 style={{ marginTop: 0 }}>Getrennt bevorratet</h4>
        <p style={{ fontSize: 13, margin: 0 }}>
          Sprinklernetz und Steigleitung haben eigene Vorräte. Sie werden
          <strong> nicht addiert</strong> und nicht gegeneinander gerechnet —
          das ist die Festlegung, bestätigt am 16.09.2026 (Frage 47). Wären
          sie gemeinsam bevorratet, wäre die nächste Frage, ob gleichzeitig
          gefordert ist, und die hätte das Brandschutzkonzept zu beantworten,
          nicht die Rechnung.
        </p>
      </div>

      <Grundlagen u={u} />
    </>
  );
}


/**
 * Die Grundlagen: was der Fachplaner am 16.09.2026 festgelegt hat.
 *
 * --- Warum das auf die Seite gehört ----------------------------------------
 *
 * Wer einen Druckbedarf von 48,21 bar liest, muss sehen können, WARUM mit
 * der Wurzelregel gerechnet wird, seit wann und auf wessen Wort. Eine
 * Grundlage, die nur in einer Migration steht, findet niemand — und dann
 * wird die Zahl angezweifelt statt die Festlegung nachgelesen.
 *
 * --- Warum das Offene dazugehört -------------------------------------------
 *
 * Eine Liste, die nur die beantworteten Fragen zeigt, sieht vollständig aus.
 * Die Pumpenkennlinien sind es nicht — und genau das ist die Zeile, die ein
 * Prüfer sehen muss.
 */
function Grundlagen({ u }: { u: any }) {
  const alle: any[] = u.festlegungen ?? [];

  if (alle.length === 0) return null;

  const offen = alle.filter((f) => !f.istBestaetigt);
  const anweisungen: any[] = u.anweisungen ?? [];

  const wert = (f: any) => {
    const zahl = f.zahlwert == null ? null
      : f.zahlwert.toLocaleString('de-DE', { maximumFractionDigits: 2 });

    return [zahl, f.einheit, f.textwert].filter(Boolean).join(' ');
  };

  return (
    <>
      <div className="karte">
        <h3 style={{ marginTop: 0 }}>
          Festlegungen des Fachplaners vom 16.09.2026
        </h3>

        <p className="schwach" style={{ fontSize: 12, marginTop: 0 }}>
          Die Antworten auf die fünfzehn offenen Fragen der Normprüfung
          (Fragen 34 bis 48). Jede Zeile trägt ihre Quelle und den Wortlaut
          der Antwort — eine Entscheidung lässt sich nicht nachrechnen, sie
          gilt, weil jemand sie getroffen hat.
        </p>

        <table className="tabelle">
          <thead>
            <tr>
              <th style={{ width: 50 }}>Frage</th>
              <th>Gegenstand</th>
              <th style={{ width: 150 }}>Wert</th>
              <th style={{ width: 110 }}>Stand</th>
            </tr>
          </thead>
          <tbody>
            {alle.map((f) => (
              <tr key={f.id} title={f.wortlaut ?? ''}>
                <td>{f.frage}</td>
                <td>
                  {f.schluessel}
                  {f.zielfeld && (
                    <div className="schwach" style={{ fontSize: 11 }}>
                      wirkt an {f.zielfeld}
                    </div>
                  )}
                </td>
                <td><strong>{wert(f)}</strong></td>
                <td>
                  {f.istBestaetigt
                    ? <span>bestätigt</span>
                    : <strong style={{ color: '#b45309' }}>offen</strong>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {offen.length > 0 && (
          <p style={{ fontSize: 13, marginBottom: 0 }}>
            <strong>{offen.length} Festlegung(en) offen:</strong>{' '}
            {offen.map((f) => f.begruendung).join(' ')}
          </p>
        )}
      </div>

      {anweisungen.length > 0 && (
        <div className="karte">
          <h3 style={{ marginTop: 0 }}>Arbeitsvorrat Modellpflege</h3>

          <p className="schwach" style={{ fontSize: 12, marginTop: 0 }}>
            Die Fragen 44, 45 und 48 sind als Modellfehler bestätigt. Damit
            ist aus dem Befund ein Arbeitsvorrat geworden — eine Zeile je
            Handgriff, nicht ein Satz mit einer Zahl darin.
          </p>

          <table className="tabelle">
            <thead>
              <tr>
                <th>Art</th>
                <th style={{ width: 60 }}>Frage</th>
                <th style={{ width: 90 }}>Gewicht</th>
                <th style={{ width: 90 }}>Zeilen</th>
              </tr>
            </thead>
            <tbody>
              {anweisungen.map((a) => (
                <tr key={a.art}>
                  <td>{a.art}</td>
                  <td>{a.frage}</td>
                  <td>{a.gewicht}</td>
                  <td><strong>
                    {a.zeilen.toLocaleString('de-DE')}
                  </strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}


/**
 * Die Rohrnetzberechnung mit ihrer Strangansicht.
 *
 * --- Warum die Befunde vor dem Ergebnis stehen -----------------------------
 *
 * Wer eine Ergebniszeile liest und dort eine Zahl findet, hält sie für
 * belastbar. Steht drei Bildschirmhöhen weiter unten, dass die Hälfte der
 * Köpfe nicht angebunden ist, ändert das an dem Eindruck nichts mehr.
 *
 * --- Warum die Strangansicht dazugehört ------------------------------------
 *
 * Nicht die Endzahl überzeugt einen Prüfer, sondern der Weg dorthin:
 * Teilstrecke für Teilstrecke von der Alarmventilstation zum ungünstigsten
 * Sprinkler, mit Strom, Geschwindigkeit, Druckverlust und kumuliertem Druck.
 * Das ist der Aufbau, den eine Fachberechnung ausgibt.
 */
function Berechnung({ api, projektId, bereich = 'rohrnetz' }: {
  api: ApiClient; projektId: string; bereich?: string;
}) {
  const [d, setD] = useState<any | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = () => api.sprinklerBerechnung(projektId, bereich)
    .then((x) => { setD(x); setFehler(null); })
    .catch(() => setD(null));

  useEffect(() => { laden(); }, [api, projektId, bereich]);

  const z = (x: number, n = 0) =>
    (x ?? 0).toLocaleString('de-DE', {
      minimumFractionDigits: n, maximumFractionDigits: n,
    });

  const rechnen = async () => {
    setLaeuft('rechnen');
    setFehler(null);
    try {
      await api.sprinklerRechnen(projektId);
      await laden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  const pdf = async () => {
    setLaeuft('pdf');
    try {
      await api.sprinklerBerichtPdf(projektId, bereich);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  const w = d?.kennzahlen?.wirkflaeche;
  const st = d?.kennzahlen?.steigleitung;
  const v = d?.kennzahlen?.bevorratung;

  const hinweise: string[] = d?.hinweise ?? [];
  const blocker = hinweise.filter((h) => h.startsWith('BLOCKER'));
  const rest = hinweise.filter((h) => !h.startsWith('BLOCKER'));

  return (
    <>
      <div className="karte">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                      flexWrap: 'wrap' }}>
          <button className="betont" disabled={laeuft !== null}
                  onClick={rechnen}>
            {laeuft === 'rechnen' ? 'rechnet …' : 'Rohrnetz berechnen'}
          </button>

          <button disabled={laeuft !== null || !d} onClick={pdf}>
            <IconHerunterladen size={16} />
            {laeuft === 'pdf' ? 'wird erzeugt …' : 'Berechnung als PDF'}
          </button>

          <span className="schwach" style={{ fontSize: 12,
                                             marginLeft: 'auto' }}>
            {d ? `${d.norm} · ${new Date(d.gerechnetAm)
                  .toLocaleString('de-DE')}` : 'noch nicht gerechnet'}
          </span>
        </div>

        {fehler && (
          <div className="hinweis fehler" style={{ marginTop: 10 }}>
            {fehler}
          </div>
        )}
      </div>

      {!d && (
        <div className="karte">
          <div className="leer">
            {bereich === 'steigleitung' ? (
              <>
                Noch keine Berechnung der Steigleitung. Sie wird ab ihrer
                EIGENEN Einspeisung gerechnet — dem Fußventil auf der
                Saugseite der beiden Pumpen in E0, nicht ab einer
                Alarmventilstation: Eine Wandhydrantenleitung hat keine, sie
                wird von Hand geöffnet.
              </>
            ) : (
              <>
                Noch keine Berechnung. Der Lauf baut die Topologie aus den
                Verbindungen auf, sucht die Wege von jeder
                Alarmventilstation zu jedem Sprinkler und lässt die
                Wirkfläche über das Netz wandern.
              </>
            )}
          </div>
        </div>
      )}

      {/* Ist diese Rechnung noch die des Modells?

          Die Aufnahme löscht den Bestand bei jeder Übernahme und legt ihn
          neu an; die Berechnung überlebt und zeigt danach auf Element-Ids,
          die es nicht mehr gibt. Im Bestand stand eine Rechnung vom 19.08.
          neben einem Modell vom 05.09. — siebzehn Tage, und nichts sagte
          es. Der Vergleich kommt aus der API, damit es ihn nicht zweimal
          gibt. */}
      {d?.modellNeuerAlsRechnung && (
        <div className="karte" style={{ borderLeft: '3px solid #d97706' }}>
          <h3 style={{ marginTop: 0 }}>Rechenstand älter als das Modell</h3>
          <p style={{ fontSize: 13, margin: 0 }}>
            Rechnung vom{' '}
            <strong>{new Date(d.gerechnetAm).toLocaleString('de-DE')}</strong>,
            Modell zuletzt gelesen am{' '}
            <strong>
              {new Date(d.modellGelesenAm).toLocaleString('de-DE')}
            </strong>
            {' '}— <strong>neu rechnen</strong>, bevor diese Zahlen verwendet
            werden. Die Aufnahme legt den Bestand jedes Mal neu an; die
            Teilstrecken können auf Bauteile zeigen, die es nicht mehr gibt.
          </p>
        </div>
      )}

      {blocker.length > 0 && (
        <div className="karte" style={{ borderLeft: '3px solid var(--zustand-fehler)' }}>
          <h3 style={{ marginTop: 0 }}>
            Diese Berechnung ist noch kein Nachweis
          </h3>
          {blocker.map((h, i) => (
            <p key={i} style={{ fontSize: 13 }}>{h}</p>
          ))}
        </div>
      )}

      {w && (
        <div className="karte">
          <h3 style={{ marginTop: 0 }}>Maßgebende Wirkfläche</h3>

          <div className="kennzahlen">
            <div className="kennzahl">
              <div className="kennzahl-wert">{z(w.druckbedarfBar, 2)}</div>
              <div className="kennzahl-name">bar Druckbedarf</div>
            </div>
            <div className="kennzahl">
              <div className="kennzahl-wert">{z(w.volumenstromLMin)}</div>
              <div className="kennzahl-name">l/min</div>
            </div>
            <div className="kennzahl">
              <div className="kennzahl-wert">{z(w.koepfe)}</div>
              <div className="kennzahl-name">
                Sprinkler auf {z(w.flaecheM2)} m²
              </div>
            </div>
            <div className="kennzahl">
              <div className="kennzahl-wert">{z(w.weglaengeM, 1)}</div>
              <div className="kennzahl-name">
                m Weglänge über {z(w.wegstrecken)} Bauteile
              </div>
            </div>
          </div>

          <table className="tabelle" style={{ marginTop: 10 }}>
            <tbody>
              <tr><td>Ort</td>
                  <td>{w.geschoss} {w.raumnummer} {w.raumname}</td></tr>
              {w.brandrisiko && (
                <tr><td>Gefahrenklasse</td>
                    <td>
                      <strong>{w.brandrisiko}</strong>
                      {' '}— {z(w.beaufschlagungMmMin, 2)} mm/min über{' '}
                      {z(w.bemessungsdauerMin)} min,{' '}
                      {z(w.schutzflaecheJeKopfM2, 2)} m² Schutzfläche je Kopf
                    </td></tr>
              )}
              <tr><td>Sprinklerdruck am ungünstigsten Kopf</td>
                  <td>{z(w.sprinklerdruckBar, 2)} bar</td></tr>
              <tr><td>Reibung und Einzelwiderstände</td>
                  <td>{z(w.reibungBar, 2)} bar</td></tr>
              <tr><td>geodätische Höhe ({z(w.hoeheUeberStationM, 1)} m)</td>
                  <td>{z(w.geodaetischBar, 2)} bar</td></tr>
            </tbody>
          </table>
        </div>
      )}

      {st && (
        <div className="karte">
          <h3 style={{ marginTop: 0 }}>Nasse Steigleitung</h3>

          <div className="kennzahlen">
            <div className="kennzahl">
              <div className="kennzahl-wert">{z(st.druckbedarfBar, 2)}</div>
              <div className="kennzahl-name">bar Druckbedarf</div>
            </div>
            <div className="kennzahl">
              <div className="kennzahl-wert">{z(st.volumenstromLMin)}</div>
              <div className="kennzahl-name">l/min</div>
            </div>
            <div className="kennzahl">
              <div className="kennzahl-wert">
                {z(st.angebunden)}/{z(st.hydranten)}
              </div>
              <div className="kennzahl-name">Hydranten angebunden</div>
            </div>
            <div className="kennzahl">
              <div className="kennzahl-wert">{z(st.weglaengeM, 1)}</div>
              <div className="kennzahl-name">m zum ungünstigsten</div>
            </div>
          </div>

          <table className="tabelle" style={{ marginTop: 10 }}>
            <tbody>
              <tr><td>gleichzeitige Entnahmen</td>
                  <td>{z(st.gleichzeitig)} × {z(st.literProMinuteJe)} l/min
                      — die drei ungünstigsten, nicht drei beliebige</td></tr>
              <tr><td>Mindestfließdruck</td>
                  <td>{z(st.mindestfliessdruckBar, 1)} bar an der
                      ungünstigsten Entnahmestelle (DIN 14462)</td></tr>
              <tr><td>Reibung und Einzelwiderstände</td>
                  <td>{z(st.reibungBar, 2)} bar</td></tr>
              <tr><td>geodätische Höhe
                      ({z(st.hoeheUeberEinspeisungM, 1)} m)</td>
                  <td>{z(st.geodaetischBar, 2)} bar</td></tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Die Vergleichsrechnungen.

          Vier Festlegungen der Sprinklerbemessung sind nicht entschieden:
          das Rechenverfahren, die Form der Wirkfläche, die Schutzfläche je
          Kopf und die Basis der Innendurchmesser. Eine Seite, die nur die
          gerechnete Fassung zeigt, lässt den Prüfer glauben, es gäbe keine
          zweite. */}
      {d?.kennzahlen?.vergleiche?.length > 0 && (
        <div className="karte">
          <h3 style={{ marginTop: 0 }}>
            Vergleichsrechnungen — was die Alternative ergäbe
          </h3>

          <div style={{ overflowX: 'auto' }}>
            <table className="tabelle">
              <thead>
                <tr>
                  <th>Festlegung</th><th>gerechnet</th>
                  <th>Alternative</th><th>Unterschied</th>
                </tr>
              </thead>
              <tbody>
                {d.kennzahlen.vergleiche.map((g: any, i: number) => (
                  <tr key={i}>
                    <td><strong>{g.gegenstand}</strong></td>
                    <td>{g.bestand}</td>
                    <td>{g.alternative}</td>
                    <td>{g.unterschied}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {d.kennzahlen.vergleiche
            .filter((g: any) => g.bemerkung)
            .map((g: any, i: number) => (
              <p key={i} className="schwach"
                 style={{ fontSize: 12, marginBottom: 4 }}>
                <strong>{g.gegenstand}:</strong> {g.bemerkung}
              </p>
            ))}
        </div>
      )}

      {v && (
        <div className="karte">
          <h3 style={{ marginTop: 0 }}>Bevorratung und Pumpen</h3>
          <table className="tabelle">
            <tbody>
              <tr><td>Sprinkler</td><td>{z(v.sprinklerM3, 1)} m³</td></tr>
              <tr><td>Steigleitung</td><td>{z(v.steigleitungM3, 1)} m³</td></tr>
              {/* "Gesamt" ist das MAXIMUM und nicht die Summe: Sprinkler
                  und Wandhydranten werden getrennt bevorratet (S-14). */}
              <tr><td><strong>größerer von beiden</strong></td>
                  <td><strong>{z(v.gesamtM3, 1)} m³</strong>
                      {' '}— nicht addiert</td></tr>

              {/* Die zweite Auswahlregel: Der Behälter folgt Strom ×
                  Laufzeit, nicht dem Druck. Die Laufzeit hängt an der
                  Gefahrenklasse, und deshalb ist die vorratsmaßgebende
                  Fläche nicht immer die druckmaßgebende. */}
              {v.vorratMassgebendM3 != null && (
                <tr>
                  <td><strong>Vorratsbedarf (max)</strong></td>
                  <td>
                    <strong>{z(v.vorratMassgebendM3, 1)} m³</strong>
                    {v.vorratIstDruckflaeche
                      ? ' — dieselbe Fläche wie die druckmaßgebende'
                      : ' — größter Bedarf über alle Wirkflächen: '
                        + `${v.vorratBrandrisiko}, `
                        + `${z(v.vorratStromLMin)} l/min × `
                        + `${z(v.vorratDauerMin)} min in ${v.vorratOrt}`}
                  </td>
                </tr>
              )}
              <tr><td>Pumpen</td><td>{z(v.pumpen)}</td></tr>
              <tr>
                <td>n-1</td>
                <td style={{ color: v.nMinusEinsErfuellt
                  ? undefined : 'var(--zustand-fehler)' }}>
                  {v.nMinusEinsErfuellt ? 'erfüllt' : 'NICHT erfüllt'}
                  {v.nMinusEinsBefund && ` — ${v.nMinusEinsBefund}`}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {d?.strang?.length > 0 && (
        <div className="karte">
          <h3 style={{ marginTop: 0 }}>
            Strangansicht — {d.strang.length} Teilstrecken,{' '}
            {d.beanstandet} beanstandet
          </h3>

          {/* Die Zusammenfassung des Vorschlags, bevor die Tabelle kommt.
              Wer die 300 Zeilen nicht liest, soll die Aussage trotzdem
              haben. */}
          {d.strang.some((t: any) => t.vorschlagNennweite) && (
            <p style={{ fontSize: 12, color: 'var(--zustand-fehler)', marginTop: 0 }}>
              <strong>
                {d.strang.filter((t: any) => t.vorschlagNennweite).length}
                {' '}Teilstrecken brauchen eine größere Nennweite.
              </strong>
              {' '}Rot steht, was einzubauen ist, damit die Rechnung aufgeht —
              und daneben der Druckverlust, der sich damit ergäbe. Die
              Untergrenze DN25 ist eine Festlegung und keine Rechnung: Ein
              Abgang mit 52 l/min käme rechnerisch auch mit DN15 aus.
            </p>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table className="tabelle">
              <thead>
                <tr>
                  <th>Nr</th><th>DN</th>
                  <th style={{ color: 'var(--zustand-fehler)' }}>DN soll</th>
                  <th>di</th><th>L [m]</th>
                  <th>Q [l/min]</th><th>v [m/s]</th><th>Δp [bar]</th>
                  <th style={{ color: 'var(--zustand-fehler)' }}>Δp soll</th>
                  <th>geod.</th><th>Σ p [bar]</th><th>Bewertung</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  let summe = 0;
                  return d.strang.map((t: any) => {
                    summe += (t.druckverlustBar ?? 0)
                           + (t.geodaetischBar ?? 0);
                    return (
                      <tr key={t.id}>
                        <td>{t.nr}</td>

                        {/* Die verbaute Nennweite — rot, wenn sie nicht
                            reicht. Die Zahl selbst ist der Befund. */}
                        <td style={{
                          color: t.vorschlagNennweite ? 'var(--zustand-fehler)' : undefined,
                          fontWeight: t.vorschlagNennweite ? 600 : 400,
                        }}>
                          {t.nennweite ?? '—'}
                        </td>

                        {/* ══════════════════════════════════════════════
                            DIE NENNWEITE, DIE SIE HABEN MÜSSTE

                            Ein Weg und keine Auswahl: Es gibt genau eine
                            kleinste Nennweite, die hält. Drei Vorschläge
                            hießen, das System wisse es nicht — und dann
                            prüft der Planer, was das System hätte prüfen
                            können. */}
                        <td style={{ color: 'var(--zustand-fehler)', fontWeight: 600 }}
                            title={t.vorschlagGrund ?? undefined}>
                          {t.vorschlagNennweite
                            ? (t.vorschlagNennweite > 0
                                ? `DN${t.vorschlagNennweite}`
                                : 'Netz!')
                            : ''}
                        </td>

                        <td>{z(t.innendurchmesserMm)}</td>
                        <td>{z(t.laengeM, 2)}</td>
                        <td>{z(t.volumenstromLMin)}</td>
                        <td style={{
                          color: t.bewertung === 'ok' ? undefined : 'var(--zustand-fehler)',
                        }}>
                          {z(t.geschwindigkeitMs, 2)}
                        </td>
                        <td>{z(t.druckverlustBar, 4)}</td>

                        {/* Der Druckverlust MIT der vorgeschlagenen
                            Nennweite. Ein Vorschlag ohne Nachrechnung ist
                            keiner. */}
                        <td style={{ color: 'var(--zustand-fehler)' }}>
                          {t.vorschlagNennweite
                            ? z(t.vorschlagDruckverlustBar, 4) : ''}
                        </td>

                        <td>{z(t.geodaetischBar, 4)}</td>
                        <td>{z(summe, 3)}</td>
                        <td style={{ color: t.bewertung === 'ok'
                          ? undefined : '#b45309' }}>
                          {t.vorschlagGrund ?? t.befund ?? t.bewertung}
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(d?.annahmen?.length > 0 || rest.length > 0) && (
        <div className="karte">
          <h3 style={{ marginTop: 0 }}>Annahmen und Befunde</h3>

          {(d?.annahmen ?? []).map((a: string, i: number) => (
            <p key={i} className="schwach" style={{ fontSize: 12 }}>· {a}</p>
          ))}

          {rest.map((h, i) => (
            <p key={`h${i}`} style={{ fontSize: 12 }}>· {h}</p>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Der Nachweisbaum (Schritt 4c).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ANLAGE → STATION → WIRKFLÄCHE / STEIGLEITUNG → WEG → TEILSTRECKE → POSTEN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Dieselbe Form wie beim Druckverlust der Lüftung: links der Baum, rechts der
 * gewählte Knoten. Jede Stufe beantwortet eine Frage, die die darüberliegende
 * offen lässt — und die letzte, die Postenliste, ist die, die ein Prüfer
 * wirklich liest: Bauteil für Bauteil, mit Beiwert, Herkunft und laufender
 * Summe.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE KÖPFE OHNE WEG STEHEN AN IHRER STATION
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 2.898 von 5.351 Sprinklern gehören keiner Station an: 2.170 Platzhalter
 * ohne verbundenen Connector und 728, die angeschlossen sind und trotzdem
 * keinen Weg zu einer Station haben. Sie bekommen einen eigenen Ast. Sie
 * wegzulassen hieße, mehr als die Hälfte der Anlage unsichtbar zu machen —
 * und die Rechnung sähe vollständiger aus, als sie ist.
 */
function Nachweisbaum({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const [anlage, setAnlage] = useState<any | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  // Was rechts steht.
  const [wahl, setWahl] = useState<Wahl>({ art: 'anlage' });

  // Was im Baum aufgeklappt ist.
  const [offen, setOffen] = useState<Set<string>>(new Set(['wirkflaeche']));

  const [wirk, setWirk] = useState<any | null>(null);
  const [weg, setWeg] = useState<any | null>(null);
  const [posten, setPosten] = useState<any | null>(null);
  const [koepfe, setKoepfe] = useState<any | null>(null);
  const [steig, setSteig] = useState<any | null>(null);
  const [befunde, setBefunde] = useState<any | null>(null);
  const [nurOhneWeg, setNurOhneWeg] = useState(false);

  useEffect(() => {
    api.sprinklerAnlage(projektId)
      .then((x) => { setAnlage(x); setFehler(null); })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));

    api.sprinklerBefundliste(projektId).then(setBefunde).catch(() => {});

    // Die Wirkfläche wird sofort geladen: Sie ist der Knoten, auf den es
    // ankommt, und der Baum zeigt ihre Köpfe als dritte Ebene.
    api.sprinklerWirkflaeche(projektId).then(setWirk).catch(() => setWirk(null));
  }, [api, projektId]);

  // Der Weg des gewählten Kopfes.
  useEffect(() => {
    if (wahl.art !== 'weg' && wahl.art !== 'teilstrecke') return;

    setWeg(null);
    api.sprinklerWeg(projektId, wahl.kopf!).then(setWeg).catch(() => setWeg(null));
  }, [api, projektId, wahl.art === 'weg' || wahl.art === 'teilstrecke'
      ? wahl.kopf : null]);

  // Die Posten der gewählten Teilstrecke.
  useEffect(() => {
    if (wahl.art !== 'teilstrecke') { setPosten(null); return; }

    setPosten(null);
    api.sprinklerWegposten(projektId, wahl.kopf!, wahl.nr!)
      .then(setPosten).catch(() => setPosten(null));
  }, [api, projektId, wahl.art, wahl.kopf, wahl.nr]);

  // Die Köpfe der gewählten Station.
  useEffect(() => {
    if (wahl.art !== 'station') return;

    setKoepfe(null);
    api.sprinklerStationskoepfe(projektId, wahl.station!, {
      ohneWeg: nurOhneWeg, proSeite: 200,
    }).then(setKoepfe).catch(() => setKoepfe(null));
  }, [api, projektId, wahl.art, wahl.station, nurOhneWeg]);

  // Die Steigleitung.
  useEffect(() => {
    if (wahl.art !== 'steigleitung' || steig) return;

    api.sprinklerSteigstrang(projektId).then(setSteig).catch(() => setSteig(null));
  }, [api, projektId, wahl.art]);

  if (fehler) return <div className="hinweis fehler">{fehler}</div>;
  if (!anlage) return <div className="karte"><div className="leer">wird geladen …</div></div>;

  if (anlage.fehlgrund) {
    return (
      <div className="karte">
        <div className="leer">{anlage.fehlgrund}</div>
      </div>
    );
  }

  const umschalten = (k: string) => {
    const neu = new Set(offen);
    if (neu.has(k)) neu.delete(k); else neu.add(k);
    setOffen(neu);
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr',
                  gap: 18, alignItems: 'start' }}>
      <div className="karte" style={{ padding: 0, position: 'sticky', top: 16 }}>
        <div className="baum" style={{ maxHeight: '78vh', overflowY: 'auto' }}>
          {/* Die Anlage */}
          <button
            className={`baum-ebene ${wahl.art === 'anlage' ? 'aktiv' : ''}`}
            onClick={() => setWahl({ art: 'anlage' })}
          >
            <span style={{ fontWeight: 600 }}>Sprinkleranlage</span>
            <span className="schwach" style={{ marginLeft: 'auto' }}>
              {z(anlage.bilanz?.koepfe)} Köpfe
            </span>
          </button>

          {/* Die maßgebende Wirkfläche - vor den Stationen: Sie ist der
              Nachweis, die Stationen sind der Bestand. */}
          {anlage.wirkflaeche && (
            <>
              <button
                className={`baum-zeile ${wahl.art === 'wirkflaeche' ? 'aktiv' : ''}`}
                style={{ paddingLeft: 22 }}
                onClick={() => { setWahl({ art: 'wirkflaeche' });
                                 umschalten('wirkflaeche'); }}
              >
                <span className="marke schlecht">maßgebend</span>
                <span style={{ minWidth: 0, overflow: 'hidden',
                               textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  Wirkfläche {anlage.wirkflaeche.raumnummer ?? ''}
                </span>
                <span style={{ marginLeft: 'auto', fontWeight: 600 }}>
                  {z(anlage.wirkflaeche.druckbedarfBar, 2)} bar
                </span>
              </button>

              {offen.has('wirkflaeche') && wirk?.koepfe?.map((k: any) => (
                <div key={k.elementId}>
                  <button
                    className={`baum-zeile ${
                      (wahl.art === 'weg' || wahl.art === 'teilstrecke')
                      && wahl.kopf === k.elementId ? 'aktiv' : ''}`}
                    style={{ paddingLeft: 38 }}
                    onClick={() => { setWahl({ art: 'weg', kopf: k.elementId });
                                     umschalten(`w${k.elementId}`); }}
                  >
                    <span className="sehr-schwach">{k.elementId}</span>
                    <span style={{ minWidth: 0, overflow: 'hidden',
                                   textOverflow: 'ellipsis',
                                   whiteSpace: 'nowrap' }}>
                      {k.raumnummer ?? 'ohne Raum'}
                    </span>
                    {k.istUnguenstigster && (
                      <span className="marke schlecht">ungünstigster</span>
                    )}
                    <span style={{ marginLeft: 'auto' }}>
                      {z(k.druckbedarfBar, 2)} bar
                    </span>
                  </button>

                  {/* Die Teilstrecken dieses Wegs - nur unter dem gewählten
                      Kopf: Sie werden geladen und nicht vorgehalten. */}
                  {offen.has(`w${k.elementId}`) && wahl.kopf === k.elementId
                   && weg?.teilstrecken?.map((t: any) => (
                    <button
                      key={t.nr}
                      className={`baum-zeile ${
                        wahl.art === 'teilstrecke' && wahl.nr === t.nr
                          ? 'aktiv' : ''}`}
                      style={{ paddingLeft: 54 }}
                      onClick={() => setWahl({
                        art: 'teilstrecke', kopf: k.elementId, nr: t.nr })}
                    >
                      <span className="sehr-schwach">TS {t.nr}</span>
                      <span>DN{t.nennweite ?? '—'}</span>
                      {t.bewertung !== 'ok' && (
                        <span className="marke schlecht">v</span>
                      )}
                      <span style={{ marginLeft: 'auto' }}>
                        {z(t.druckverlustBar, 3)} bar
                      </span>
                    </button>
                  ))}
                </div>
              ))}
            </>
          )}

          {/* Die nasse Steigleitung - eine eigene Anlage nach eigenem
              Regelwerk. */}
          <button
            className={`baum-zeile ${wahl.art === 'steigleitung' ? 'aktiv' : ''}`}
            style={{ paddingLeft: 22 }}
            onClick={() => setWahl({ art: 'steigleitung' })}
          >
            <span className="marke neutral">DIN 14462</span>
            <span>Nasse Steigleitung</span>
          </button>

          <div className="baum-abschnitt">Alarmventilstationen</div>

          {anlage.stationen?.map((s: any) => (
            <button
              key={s.elementId}
              className={`baum-zeile ${
                wahl.art === 'station' && wahl.station === s.elementId
                  ? 'aktiv' : ''}`}
              style={{ paddingLeft: 22 }}
              onClick={() => setWahl({ art: 'station', station: s.elementId })}
            >
              <span style={{ minWidth: 0, overflow: 'hidden',
                             textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.kennung}
              </span>
              {s.istMassgebend && (
                <span className="marke schlecht">maßgebend</span>
              )}
              {s.koepfe === 0 && (
                <span className="marke warnung">leer</span>
              )}
              <span className="schwach" style={{ marginLeft: 'auto' }}>
                {z(s.koepfe)}
              </span>
            </button>
          ))}

          {/* Der Ast, den man nicht verstecken darf. */}
          <button
            className={`baum-zeile ${
              wahl.art === 'station' && wahl.station === 0 ? 'aktiv' : ''}`}
            style={{ paddingLeft: 22 }}
            onClick={() => setWahl({ art: 'station', station: 0 })}
          >
            <span className="marke warnung">ohne Station</span>
            <span className="schwach" style={{ marginLeft: 'auto' }}>
              {z(anlage.ohneStation?.koepfe)}
            </span>
          </button>
        </div>
      </div>

      <div>
        {wahl.art === 'anlage' && (
          <Anlagenknoten anlage={anlage} befunde={befunde} />
        )}

        {wahl.art === 'wirkflaeche' && (
          <Wirkflaechenknoten wirk={wirk} waehleKopf={(id: number) =>
            { setWahl({ art: 'weg', kopf: id });
              setOffen(new Set([...offen, 'wirkflaeche', `w${id}`])); }} />
        )}

        {wahl.art === 'weg' && (
          <Wegknoten weg={weg} waehleTeilstrecke={(nr: number) =>
            setWahl({ art: 'teilstrecke', kopf: wahl.kopf, nr })} />
        )}

        {wahl.art === 'teilstrecke' && (
          <Postenknoten daten={posten} zurueck={() =>
            setWahl({ art: 'weg', kopf: wahl.kopf })} />
        )}

        {wahl.art === 'station' && (
          <Stationsknoten
            koepfe={koepfe}
            station={anlage.stationen?.find(
              (s: any) => s.elementId === wahl.station)}
            nurOhneWeg={nurOhneWeg}
            setNurOhneWeg={setNurOhneWeg}
          />
        )}

        {wahl.art === 'steigleitung' && <Steigknoten steig={steig} />}
      </div>
    </div>
  );
}

type Wahl = {
  art: 'anlage' | 'station' | 'wirkflaeche' | 'weg' | 'teilstrecke'
     | 'steigleitung';
  station?: number;
  kopf?: number;
  nr?: number;
};

/** Eine Zahl, deutsch. */
function z(x: number | null | undefined, n = 0): string {
  return x === null || x === undefined ? '—'
    : x.toLocaleString('de-DE',
        { minimumFractionDigits: n, maximumFractionDigits: n });
}

/**
 * Die Herkunft eines Eingangswerts als Marke — dieselben Farben wie bei der
 * Lüftung.
 *
 * Grün: aus dem Modell. Gelb: aus einer Regel abgeleitet (Familie und Typ).
 * Rot: gar keine — dann fehlt der Beiwert und mit ihm der Anteil in der
 * Summe.
 */
function Herkunftsmarke({ wert }: { wert: string | null | undefined }) {
  if (!wert) return <span className="sehr-schwach">·</span>;

  const klasse = wert === 'modell' ? 'marke gut'
    : wert === 'keine' ? 'marke schlecht' : 'marke warnung';

  const kurz = wert === 'modell' ? 'M'
    : wert === 'regel' ? 'R' : wert === 'keine' ? '—' : wert[0].toUpperCase();

  return <span className={klasse} title={HERKUNFTSTEXT[wert] ?? wert}>
    {kurz}
  </span>;
}

/**
 * Was eine Herkunft bedeutet — ausgeschrieben, als Titel der Marke.
 *
 * „F“ allein sagt niemandem, dass hinter einer Auslösetemperatur eine
 * Entscheidung vom 16.09.2026 steht und keine Messung. Genau dieser
 * Unterschied ist der Grund, aus dem die Herkunft überhaupt angezeigt wird.
 */
const HERKUNFTSTEXT: Record<string, string> = {
  modell: 'aus dem Modell gelesen',
  regel: 'aus Familie und Typ abgeleitet',
  keine: 'keine — der Beiwert fehlt',
  festlegung:
    'Festlegung des Fachplaners vom 16.09.2026 („68°C standard generell“) '
    + '— nicht aus dem Modell',
  grundwert: 'Grundwert der Projektfestlegung vom 16.08.2026',
  temperaturklasse: 'aus dem Typparameter Temperaturklasse gelesen',
  temperaturklasse_farbe:
    'über die Kennfarbe der Glasfassampulle nach VdS CEA 4001 übersetzt',
};

/** Die Anlage: Bilanz, Gefahrenklassen, Vergleiche, Bevorratung, Befunde. */
function Anlagenknoten({ anlage, befunde }: { anlage: any; befunde: any }) {
  const v = anlage.kennzahlen?.bevorratung;
  const vergleiche = anlage.kennzahlen?.vergleiche ?? [];

  return (
    <>
      <div className="karte">
        <h3 style={{ marginTop: 0 }}>Was gerechnet wurde</h3>

        <div className="kennzahlen">
          <div className="kennzahl">
            <div className="kennzahl-wert">{z(anlage.bilanz?.gerechnet)}</div>
            <div className="kennzahl-name">
              von {z(anlage.bilanz?.koepfe)} Sprinklern gerechnet
              ({z(anlage.bilanz?.anteilProzent, 1)} %)
            </div>
          </div>
          <div className="kennzahl">
            <div className="kennzahl-wert">{z(anlage.bilanz?.platzhalter)}</div>
            <div className="kennzahl-name">Platzhalter ohne Connector</div>
          </div>
          <div className="kennzahl">
            <div className="kennzahl-wert" style={{ color: 'var(--zustand-fehler)' }}>
              {z(anlage.bilanz?.ohneWeg)}
            </div>
            <div className="kennzahl-name">
              angeschlossen, aber ohne Weg zur Station
            </div>
          </div>
          <div className="kennzahl">
            <div className="kennzahl-wert">{z(anlage.stationen?.length)}</div>
            <div className="kennzahl-name">
              Alarmventilstationen, davon{' '}
              {z(anlage.stationen?.filter((s: any) => s.koepfe === 0).length)}
              {' '}ohne Sprinkler
            </div>
          </div>
        </div>

        <p className="schwach" style={{ fontSize: 12, marginBottom: 0 }}>
          Die Bilanz steht oben und nicht unter der Tabelle: Die Rechnung
          heißt „maßgebende Wirkfläche“ und kennt{' '}
          {z(anlage.bilanz?.anteilProzent, 1)} % der Sprinkler. Der Rest sind
          Platzhalter und Köpfe, deren Teilnetz im Modell an keiner Station
          hängt.
        </p>
      </div>

      {/* Die Gefahrenklassen der ganzen Anlage (aus Schritt 4b). */}
      {anlage.gefahrenklassen?.length > 0 && (
        <div className="karte">
          <h3 style={{ marginTop: 0 }}>Gefahrenklassen je Kopf</h3>
          <table className="tabelle">
            <thead>
              <tr><th>Klasse</th><th>Sprinkler</th><th>Anteil</th></tr>
            </thead>
            <tbody>
              {anlage.gefahrenklassen.map((g: any) => (
                <tr key={g.klasse}>
                  <td><strong>{g.klasse}</strong></td>
                  <td>{z(g.koepfe)}</td>
                  <td>
                    {z(100 * g.koepfe / (anlage.bilanz?.koepfe || 1), 1)} %
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="schwach" style={{ fontSize: 12, marginBottom: 0 }}>
            Aus Zuordnungsregeln über die Raumnutzung abgeleitet und vom
            Fachplaner UNBESTÄTIGT. Welche Klasse ein Bereich trägt, folgt aus
            dem Brandschutzkonzept und nicht aus dem Raumnamen.
          </p>
        </div>
      )}

      {vergleiche.length > 0 && (
        <div className="karte">
          <h3 style={{ marginTop: 0 }}>
            Vergleichsrechnungen — was die Alternative ergäbe
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="tabelle">
              <thead>
                <tr><th>Festlegung</th><th>gerechnet</th>
                    <th>Alternative</th><th>Unterschied</th></tr>
              </thead>
              <tbody>
                {vergleiche.map((g: any, i: number) => (
                  <tr key={i}>
                    <td><strong>{g.gegenstand}</strong></td>
                    <td>{g.bestand}</td>
                    <td>{g.alternative}</td>
                    <td>{g.unterschied}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {v && (
        <div className="karte">
          <h3 style={{ marginTop: 0 }}>Bevorratung</h3>
          <table className="tabelle">
            <tbody>
              <tr><td>Sprinkler</td>
                  <td>{z(v.sprinklerM3, 1)} m³ — aus der druckmaßgebenden
                      Fläche, {z(v.bemessungsdauerMin)} min</td></tr>
              <tr><td>Steigleitung</td>
                  <td>{z(v.steigleitungM3, 1)} m³</td></tr>
              <tr><td><strong>größerer von beiden</strong></td>
                  <td><strong>{z(v.gesamtM3, 1)} m³</strong> — NICHT addiert:
                      getrennte Bevorratung nach Festlegung des
                      Auftraggebers</td></tr>
              {v.vorratMassgebendM3 != null && (
                <tr>
                  <td><strong>Vorratsbedarf (max)</strong></td>
                  <td>
                    <strong>{z(v.vorratMassgebendM3, 1)} m³</strong>
                    {v.vorratIstDruckflaeche
                      ? ' — dieselbe Fläche wie die druckmaßgebende'
                      : ` — Gefahrenklasse ${v.vorratBrandrisiko}, `
                        + `${z(v.vorratStromLMin)} l/min × `
                        + `${z(v.vorratDauerMin)} min in ${v.vorratOrt}`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {v.vorratMassgebendM3 != null && !v.vorratIstDruckflaeche && (
            <p style={{ fontSize: 12, marginBottom: 0 }}>
              <strong>Zwei Fragen, zwei Maxima.</strong> Die Pumpe folgt dem
              größten DRUCKBEDARF, der Behälter dem größten Produkt aus Strom
              und Laufzeit — und die Laufzeit hängt an der Gefahrenklasse. Die
              vorratsmaßgebende Fläche ist hier eine andere als die
              druckmaßgebende: {z(v.vorratMassgebendM3, 1)} statt{' '}
              {z(v.sprinklerM3, 1)} m³.
            </p>
          )}
        </div>
      )}

      {befunde?.befunde?.length > 0 && (
        <div className="karte">
          <h3 style={{ marginTop: 0 }}>
            Befunde — {z(befunde.fehler)} Fehler, {z(befunde.warnungen)}{' '}
            Warnungen
          </h3>
          <table className="tabelle">
            <thead>
              <tr><th>Schwere</th><th>Befund</th><th>Zahl</th></tr>
            </thead>
            <tbody>
              {befunde.befunde.map((b: any) => (
                <tr key={b.kennung}>
                  <td>
                    <span className={b.schwere === 'fehler' ? 'marke schlecht'
                      : b.schwere === 'warnung' ? 'marke warnung'
                      : 'marke neutral'}>{b.schwere}</span>
                  </td>
                  <td>
                    <strong>{b.titel}</strong>
                    <div className="schwach" style={{ fontSize: 12 }}>
                      {b.text}
                    </div>
                  </td>
                  <td>{z(b.zahl, b.einheit === '%' ? 1 : 0)} {b.einheit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/** Die maßgebende Wirkfläche mit jedem Kopf auf ihr. */
function Wirkflaechenknoten({ wirk, waehleKopf }: {
  wirk: any; waehleKopf: (id: number) => void;
}) {
  if (!wirk) return <div className="karte"><div className="leer">wird geladen …</div></div>;

  return (
    <div className="karte">
      <h3 style={{ marginTop: 0 }}>
        Maßgebende Wirkfläche — {wirk.geschoss} {wirk.raumnummer}
      </h3>

      <div className="kennzahlen">
        <div className="kennzahl">
          <div className="kennzahl-wert">{z(wirk.druckbedarfBar, 2)}</div>
          <div className="kennzahl-name">bar an der Station</div>
        </div>
        <div className="kennzahl">
          <div className="kennzahl-wert">{z(wirk.volumenstromLMin)}</div>
          <div className="kennzahl-name">l/min gesamt</div>
        </div>
        <div className="kennzahl">
          <div className="kennzahl-wert">{z(wirk.koepfeGefunden)}</div>
          <div className="kennzahl-name">
            Sprinkler auf {z(wirk.flaecheM2)} m²
            {wirk.koepfeGefunden !== wirk.koepfeGerechnet
              && ` (Rechnung: ${z(wirk.koepfeGerechnet)})`}
          </div>
        </div>
        <div className="kennzahl">
          <div className="kennzahl-wert">{wirk.brandrisiko}</div>
          <div className="kennzahl-name">
            {z(wirk.beaufschlagungMmMin, 2)} mm/min über{' '}
            {z(wirk.bemessungsdauerMin)} min
          </div>
        </div>
      </div>

      <p className="schwach" style={{ fontSize: 12 }}>
        Rechteck {z(wirk.kanteLangM, 1)} × {z(wirk.kanteKurzM, 1)} m um den
        Sprinkler {wirk.mittelpunktElementId}. Jeder Kopf trägt denselben
        Auslegungsstrom (Beaufschlagung ×{' '}
        {z(wirk.schutzflaecheJeKopfM2, 2)} m²); der DRUCK ist je Kopf
        verschieden — er folgt aus seinem eigenen Weg.
      </p>

      <div className="tabellen-rahmen">
        <table className="tabelle">
          <thead>
            <tr>
              <th>Element</th><th>Geschoss</th><th>Raum</th><th>K</th>
              <th>Einbaulage</th><th>Klasse</th><th>t_a °C</th>
              <th style={{ textAlign: 'right' }}>Q l/min</th>
              <th style={{ textAlign: 'right' }}>p bar</th>
              <th style={{ textAlign: 'right' }}>Weg m</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {wirk.koepfe?.map((k: any) => (
              <tr key={k.elementId}>
                <td className="sehr-schwach">{k.elementId}</td>
                <td>{k.geschoss}</td>
                <td>{k.raumnummer ?? '—'}</td>
                <td>{z(k.kFaktor)}</td>
                <td>{k.einbaulage ?? '—'}</td>
                <td>{k.brandrisiko ?? '—'}</td>
                {/* Ausdrücklich leer und nicht 0: Eine fehlende
                    Auslösetemperatur ist keine von null Grad. Und die
                    Herkunft daneben: 68 °C aus einer Festlegung und 68 °C
                    aus dem Modell sind in einer Zahlenspalte nicht zu
                    unterscheiden. */}
                <td>
                  {k.ausloesetemperaturC == null
                    ? <span className="marke warnung">fehlt</span>
                    : <>
                        {z(k.ausloesetemperaturC)}{' '}
                        <Herkunftsmarke wert={k.temperaturHerkunft} />
                      </>}
                </td>
                <td style={{ textAlign: 'right' }}>{z(k.stromLMin, 1)}</td>
                <td style={{ textAlign: 'right',
                             fontWeight: k.istUnguenstigster ? 600 : 400 }}>
                  {z(k.druckbedarfBar, 2)}
                </td>
                <td style={{ textAlign: 'right' }}>{z(k.weglaengeM, 1)}</td>
                <td>
                  <button className="zweitrangig"
                          onClick={() => waehleKopf(k.elementId)}>
                    Weg
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Der Weg zu einem Kopf, in Teilstrecken. */
function Wegknoten({ weg, waehleTeilstrecke }: {
  weg: any; waehleTeilstrecke: (nr: number) => void;
}) {
  if (!weg) return <div className="karte"><div className="leer">wird geladen …</div></div>;

  const s = weg.summe;

  return (
    <div className="karte">
      <h3 style={{ marginTop: 0 }}>
        Weg zum Sprinkler {weg.kopf.elementId}
        {weg.kopf.istUnguenstigster && (
          <span className="marke schlecht" style={{ marginLeft: 8 }}>
            ungünstigster
          </span>
        )}
      </h3>

      <div className="kennzahlen">
        <div className="kennzahl">
          <div className="kennzahl-wert">{z(weg.druckbedarfBar, 2)}</div>
          <div className="kennzahl-name">bar Druckbedarf</div>
        </div>
        <div className="kennzahl">
          <div className="kennzahl-wert">{z(s.reibungBar, 2)}</div>
          <div className="kennzahl-name">
            bar Reibung ({z(s.rohre)} Rohre)
          </div>
        </div>
        <div className="kennzahl">
          <div className="kennzahl-wert">{z(s.einzelBar, 2)}</div>
          <div className="kennzahl-name">
            bar Einzelwiderstand ({z(s.formteile)} Formstücke,{' '}
            {z(s.armaturen)} Armaturen)
          </div>
        </div>
        <div className="kennzahl">
          <div className="kennzahl-wert">{z(weg.weglaengeM, 1)}</div>
          <div className="kennzahl-name">
            m über {z(weg.bauteile)} Bauteile
          </div>
        </div>
      </div>

      <p className="schwach" style={{ fontSize: 12 }}>
        Von der Alarmventilstation {weg.stationElementId} zum Kopf. Die
        Teilstrecke ist ein Abschnitt gleichen Stroms und gleicher Nennweite;
        darunter liegen die einzelnen Bauteile. Sprinklerdruck{' '}
        {z(weg.sprinklerdruckBar, 2)} bar, geodätisch{' '}
        {z(weg.geodaetischBar, 3)} bar.
        {s.mitVorschlag > 0 && (
          <> <strong style={{ color: 'var(--zustand-fehler)' }}>
            {z(s.mitVorschlag)} Posten brauchen eine größere Nennweite.
          </strong></>
        )}
      </p>

      <div className="tabellen-rahmen">
        <table className="tabelle">
          <thead>
            <tr>
              <th>Nr</th><th>DN</th>
              <th style={{ textAlign: 'right' }}>Q l/min</th>
              <th style={{ textAlign: 'right' }}>L m</th>
              <th style={{ textAlign: 'right' }}>v m/s</th>
              <th style={{ textAlign: 'right' }}>Δp bar</th>
              <th style={{ textAlign: 'right' }}>Σ p bar</th>
              <th>Posten</th><th>Bewertung</th><th></th>
            </tr>
          </thead>
          <tbody>
            {weg.teilstrecken?.map((t: any) => (
              <tr key={t.nr}>
                <td>{t.nr}</td>
                <td>{t.nennweite ?? '—'}</td>
                <td style={{ textAlign: 'right' }}>{z(t.volumenstromLMin)}</td>
                <td style={{ textAlign: 'right' }}>{z(t.laengeM, 2)}</td>
                <td style={{ textAlign: 'right',
                             color: t.bewertung === 'ok' ? undefined : 'var(--zustand-fehler)' }}>
                  {z(t.geschwindigkeitMs, 2)}
                </td>
                <td style={{ textAlign: 'right' }}>{z(t.druckverlustBar, 4)}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>
                  {z(t.summeBar, 3)}
                </td>
                <td className="sehr-schwach">
                  {t.posten} ({t.rohre}/{t.formteile}/{t.armaturen})
                </td>
                <td>{t.bewertung}</td>
                <td>
                  <button className="zweitrangig"
                          onClick={() => waehleTeilstrecke(t.nr)}>
                    Posten
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Die Posten einer Teilstrecke — Bauteil für Bauteil. */
function Postenknoten({ daten, zurueck }: {
  daten: any; zurueck: () => void;
}) {
  if (!daten) return <div className="karte"><div className="leer">wird geladen …</div></div>;

  const t = daten.teilstrecke;

  return (
    <div className="karte">
      <div className="bereich-kopf">
        <h3 className="karte-titel">
          Teilstrecke {t.nr} — DN{t.nennweite ?? '—'},{' '}
          {z(t.volumenstromLMin)} l/min
        </h3>
        <button className="zweitrangig" onClick={zurueck}>
          zurück zum Weg
        </button>
      </div>

      <p className="schwach" style={{ fontSize: 12 }}>
        Von {t.vonElementId} bis {t.nachElementId}. Die Spalte
        <strong> Herkunft</strong> sagt, woher die Bauart stammt, aus der das ζ
        folgt: <em>M</em> aus dem Modell, <em>R</em> aus der Regel über Familie
        und Typ, <em>—</em> gar keine. Die Spalte <strong>Basis</strong> nennt,
        ob der Innendurchmesser aus dem Modell oder aus der Normreihe kommt —
        die beiden weichen um bis zu 19,9 % ab.
      </p>

      <div className="tabellen-rahmen">
        <table className="tabelle">
          <thead>
            <tr>
              <th>Nr</th><th>Art</th><th>Bauteil</th><th>Element</th>
              <th>DN</th>
              <th style={{ textAlign: 'right' }}>d_i mm</th>
              <th>Basis</th>
              <th style={{ textAlign: 'right' }}>L m</th>
              <th style={{ textAlign: 'right' }}>Q l/min</th>
              <th style={{ textAlign: 'right' }}>v m/s</th>
              <th style={{ textAlign: 'right' }}>ζ</th>
              <th>Herk.</th>
              <th style={{ textAlign: 'right' }}>C / k</th>
              <th>Verfahren</th>
              <th style={{ textAlign: 'right' }}>Δp bar</th>
              <th style={{ textAlign: 'right' }}>Σ p bar</th>
              <th>DN soll</th>
            </tr>
          </thead>
          <tbody>
            {daten.posten?.map((p: any) => (
              <tr key={`${p.nr}-${p.elementId}`} title={p.befund ?? undefined}>
                <td className="sehr-schwach">{p.nr}</td>
                <td>{p.art}</td>
                {/* Der letzte Posten eines Wegs ist der Sprinkler selbst -
                    er steht in sprinkler_koepfe und hat keine Bauart. */}
                <td>
                  {p.bauart ?? p.typ ?? p.familie
                    ?? (p.art === 'sprinkler' ? 'Sprinkler'
                        : p.art === 'rohr' ? 'Rohr' : '—')}
                </td>
                <td className="sehr-schwach">{p.elementId}</td>
                <td>{p.nennweite ?? '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  {z(p.innendurchmesserMm, 1)}
                </td>
                <td className="sehr-schwach">{p.durchmesserbasis}</td>
                <td style={{ textAlign: 'right' }}>{z(p.laengeM, 2)}</td>
                <td style={{ textAlign: 'right' }}>{z(p.volumenstromLMin)}</td>
                <td style={{ textAlign: 'right',
                             color: p.bewertung === 'ok' ? undefined : 'var(--zustand-fehler)' }}>
                  {z(p.geschwindigkeitMs, 2)}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {p.zeta == null ? '—' : z(p.zeta, 2)}
                </td>
                <td><Herkunftsmarke wert={p.herkunft} /></td>
                <td style={{ textAlign: 'right' }} className="sehr-schwach">
                  {p.cFaktor != null ? `C ${z(p.cFaktor)}`
                    : p.rauigkeitMm != null ? `k ${z(p.rauigkeitMm, 2)}` : '—'}
                </td>
                <td className="sehr-schwach">{p.verfahren}</td>
                <td style={{ textAlign: 'right' }}>{z(p.druckverlustBar, 4)}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>
                  {z(p.summeBar, 4)}
                </td>
                <td style={{ color: 'var(--zustand-fehler)', fontWeight: 600 }}
                    title={p.vorschlagGrund ?? undefined}>
                  {p.vorschlagNennweite
                    ? (p.vorschlagNennweite > 0
                        ? `DN${p.vorschlagNennweite}` : 'Netz!')
                    : ''}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={14} style={{ fontWeight: 600 }}>
                Teilstrecke gesamt
              </td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>
                {z(t.druckverlustBar, 4)}
              </td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>
                {z(t.summeBar, 4)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/** Die Sprinkler einer Station — mit und ohne Weg. */
function Stationsknoten({ koepfe, station, nurOhneWeg, setNurOhneWeg }: {
  koepfe: any; station: any; nurOhneWeg: boolean;
  setNurOhneWeg: (b: boolean) => void;
}) {
  if (!koepfe) return <div className="karte"><div className="leer">wird geladen …</div></div>;

  return (
    <div className="karte">
      <h3 style={{ marginTop: 0 }}>
        {station ? `Station ${station.kennung}` : 'Sprinkler ohne Station'}
        {' '}— {z(koepfe.gesamt)} Sprinkler
      </h3>

      <div className="kennzahlen">
        <div className="kennzahl">
          <div className="kennzahl-wert">{z(koepfe.mitWeg)}</div>
          <div className="kennzahl-name">mit Weg zur Station</div>
        </div>
        <div className="kennzahl">
          <div className="kennzahl-wert" style={{ color: 'var(--zustand-fehler)' }}>
            {z(koepfe.ohneWeg)}
          </div>
          <div className="kennzahl-name">
            angeschlossen, aber ohne Weg
          </div>
        </div>
        <div className="kennzahl">
          <div className="kennzahl-wert">{z(koepfe.platzhalter)}</div>
          <div className="kennzahl-name">Platzhalter ohne Connector</div>
        </div>
        <div className="kennzahl">
          <div className="kennzahl-wert">{z(koepfe.ohneTemperatur)}</div>
          <div className="kennzahl-name">ohne Auslösetemperatur</div>
        </div>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 6,
                      fontSize: 12, margin: '10px 0' }}>
        <input type="checkbox" checked={nurOhneWeg}
               onChange={(e) => setNurOhneWeg(e.target.checked)} />
        nur Sprinkler ohne Weg
      </label>

      {koepfe.klassen?.length > 0 && (
        <p className="schwach" style={{ fontSize: 12 }}>
          Gefahrenklassen:{' '}
          {koepfe.klassen.map((k: any) =>
            `${k.klasse} ${z(k.koepfe)}`).join(', ')}
          {' · '}
          Geschosse:{' '}
          {koepfe.jeGeschoss?.map((g: any) =>
            `${g.geschoss} ${z(g.koepfe)}`).join(', ')}
        </p>
      )}

      <div className="tabellen-rahmen">
        <table className="tabelle">
          <thead>
            <tr>
              <th>Element</th><th>Geschoss</th><th>Raum</th><th>Typ</th>
              <th>K</th><th>Klasse</th><th>t_a °C</th><th>Weg</th>
            </tr>
          </thead>
          <tbody>
            {koepfe.zeilen?.map((k: any) => (
              <tr key={k.elementId}>
                <td className="sehr-schwach">{k.elementId}</td>
                <td>{k.geschoss}</td>
                <td>{k.raumnummer ?? '—'}</td>
                <td className="sehr-schwach">{k.typ ?? '—'}</td>
                <td>{z(k.kFaktor)}</td>
                <td>{k.brandrisiko ?? '—'}</td>
                <td>
                  {k.ausloesetemperaturC == null
                    ? <span className="marke warnung">fehlt</span>
                    : <>
                        {z(k.ausloesetemperaturC)}{' '}
                        <Herkunftsmarke wert={k.temperaturHerkunft} />
                      </>}
                </td>
                <td>
                  {k.hatWeg
                    ? <span className="marke gut">
                        {z(k.weglaengeM, 1)} m
                      </span>
                    : k.angeschlossen
                      ? <span className="marke schlecht">kein Weg</span>
                      : <span className="marke neutral">Platzhalter</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {koepfe.gesamt > koepfe.zeilen?.length && (
        <p className="schwach" style={{ fontSize: 12 }}>
          … und {z(koepfe.gesamt - koepfe.zeilen.length)} weitere. Die Zähler
          oben gelten IMMER für alle.
        </p>
      )}
    </div>
  );
}

/** Die nasse Steigleitung, in Teilstrecken und Posten. */
function Steigknoten({ steig }: { steig: any }) {
  if (!steig) return <div className="karte"><div className="leer">wird geladen …</div></div>;

  return (
    <div className="karte">
      <h3 style={{ marginTop: 0 }}>
        Nasse Steigleitung — {steig.norm}
      </h3>

      <div className="kennzahlen">
        <div className="kennzahl">
          <div className="kennzahl-wert">{z(steig.summe?.summeBar, 2)}</div>
          <div className="kennzahl-name">bar über den Strang</div>
        </div>
        <div className="kennzahl">
          <div className="kennzahl-wert">{z(steig.summe?.teilstrecken)}</div>
          <div className="kennzahl-name">
            Teilstrecken aus {z(steig.summe?.posten)} Posten
          </div>
        </div>
        <div className="kennzahl">
          <div className="kennzahl-wert">{z(steig.beanstandet)}</div>
          <div className="kennzahl-name">beanstandete Posten</div>
        </div>
      </div>

      <p className="schwach" style={{ fontSize: 12 }}>
        Aus der gespeicherten Berechnung — sie wird ab ihrer EIGENEN
        Einspeisung gerechnet, dem Fußventil auf der Saugseite, und nicht ab
        einer Alarmventilstation. Der Volumenstrom ist über den ganzen Weg der
        gesamte: Drei gleichzeitige Entnahmen belasten den Strang gemeinsam.
      </p>

      <div className="tabellen-rahmen">
        <table className="tabelle">
          <thead>
            <tr>
              <th>Nr</th><th>DN</th>
              <th style={{ textAlign: 'right' }}>Q l/min</th>
              <th style={{ textAlign: 'right' }}>L m</th>
              <th style={{ textAlign: 'right' }}>v m/s</th>
              <th style={{ textAlign: 'right' }}>Δp bar</th>
              <th style={{ textAlign: 'right' }}>Σ p bar</th>
              <th>Posten</th><th>Bewertung</th>
            </tr>
          </thead>
          <tbody>
            {steig.teilstrecken?.map((x: any) => (
              <tr key={x.zeile.nr}>
                <td>{x.zeile.nr}</td>
                <td>{x.zeile.nennweite ?? '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  {z(x.zeile.volumenstromLMin)}
                </td>
                <td style={{ textAlign: 'right' }}>{z(x.zeile.laengeM, 2)}</td>
                <td style={{ textAlign: 'right' }}>
                  {z(x.zeile.geschwindigkeitMs, 2)}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {z(x.zeile.druckverlustBar, 4)}
                </td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>
                  {z(x.zeile.summeBar, 3)}
                </td>
                <td className="sehr-schwach">{x.zeile.posten}</td>
                <td>{x.zeile.bewertung}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
